import type { Location } from "@sitescene/types";

/**
 * Helpers for location / zone / sensor (L/Z/S) event filters.
 *
 * Filters store explicit ID arrays in EventsContext. "Default" means every ID
 * in the org tree for that category is selected. These utilities compare sets,
 * strip stale IDs, hydrate legacy empty arrays, and derive scoped child IDs
 * when a subset of locations is selected.
 *
 * Used by: `useValidateLocationZoneSensorFilters`, `location-zone-sensor-filter`,
 * `filter-url-params`, `are-event-filters-active`, and `useLocationZoneSensorIdSets`
 * in `hooks/useOrgLocationTree.ts`.
 */

/** Shape of the three L/Z/S filter arrays held in EventsContext. */
export type LocationZoneSensorFilterIds = {
	locationsFilter: string[];
	sensorsFilter: string[];
	zonesFilter: string[];
};

/** Flat ID lists for every location, zone, and sensor in the org tree. */
export type LocationZoneSensorIdSets = {
	locationIds: string[];
	sensorIds: string[];
	zoneIds: string[];
};

/**
 * Compares two ID arrays as unordered sets (order and duplicates ignored).
 * Null/undefined are treated as empty.
 *
 * Used by: `isAllFilterIdsSelected`, `reconcileFilterIds`,
 * `useValidateLocationZoneSensorFilters` (detect context updates after reconcile),
 * and `location-zone-sensor-filter` (pending vs committed Apply diff).
 */
export const sameIdSet = (
	a?: null | readonly string[],
	b?: null | readonly string[],
): boolean => {
	const aa = a ?? [];
	const bb = b ?? [];
	if (aa.length !== bb.length) return false;
	const bSet = new Set(bb);
	return aa.every((id) => bSet.has(id));
};

/**
 * True when `filterIds` contains every ID in `allIds` for that category.
 * Empty `filterIds` with a non-empty org tree is not "all selected".
 *
 * Used by: `isLocationZoneSensorFilterActive`, `reconcileLocationZoneSensorFilters`,
 * and `filter-url-params` (omit L/Z/S query params when selection equals the full org set).
 */
export const isAllFilterIdsSelected = (
	filterIds: string[],
	allIds: readonly string[],
): boolean => {
	if (allIds.length === 0) return filterIds.length === 0;
	return sameIdSet(filterIds, allIds);
};

/**
 * True when L/Z/S arrays are still the initial empty placeholders before
 * hydration or reconciliation against the org tree.
 */
export const isPreReconcileLocationZoneSensorPlaceholder = (
	filters: LocationZoneSensorFilterIds,
	idSets: LocationZoneSensorIdSets,
): boolean =>
	filters.locationsFilter.length === 0 &&
	filters.zonesFilter.length === 0 &&
	filters.sensorsFilter.length === 0 &&
	(idSets.locationIds.length > 0 ||
		idSets.zoneIds.length > 0 ||
		idSets.sensorIds.length > 0);

/**
 * True when any L/Z/S filter differs from selecting the entire org tree.
 * Drives active styling on the combined filter button and `hasActiveFilters` /
 * reset-button logic via `are-event-filters-active`.
 *
 * Used by: `are-event-filters-active`, `location-zone-sensor-filter`.
 */
export const isLocationZoneSensorFilterActive = (
	filters: LocationZoneSensorFilterIds,
	idSets: LocationZoneSensorIdSets,
): boolean => {
	if (isPreReconcileLocationZoneSensorPlaceholder(filters, idSets)) {
		return false;
	}

	return (
		!isAllFilterIdsSelected(filters.locationsFilter, idSets.locationIds) ||
		!isAllFilterIdsSelected(filters.zonesFilter, idSets.zoneIds) ||
		!isAllFilterIdsSelected(filters.sensorsFilter, idSets.sensorIds)
	);
};

/**
 * Sensor IDs the dashboard map should show for the current L/Z/S selection.
 *
 * Returns `undefined` when the filter is inactive (show the full org tree).
 * Otherwise uses `sensorsFilter` — the leaf selection the filter UI maintains
 * when zones/locations are checked, unchecked, or partially selected.
 *
 * Used by: dashboard map marker/overlay indexing.
 */
export const resolveMapAllowedSensorIds = (
	filters: LocationZoneSensorFilterIds,
	idSets: LocationZoneSensorIdSets,
): Set<string> | undefined => {
	if (!isLocationZoneSensorFilterActive(filters, idSets)) {
		return undefined;
	}

	return new Set(filters.sensorsFilter);
};

/**
 * True when two location ID selections represent the same filter.
 * Treats `[]` and the full org list as equivalent ("all selected").
 *
 * Used by: `watchlist-location-filter` (pending vs committed Apply diff).
 */
export const areLocationFiltersEquivalent = (
	a: readonly string[],
	b: readonly string[],
	allLocationIds: readonly string[],
): boolean => {
	const aIsAll =
		a.length === 0 || isAllFilterIdsSelected([...a], allLocationIds);
	const bIsAll =
		b.length === 0 || isAllFilterIdsSelected([...b], allLocationIds);
	if (aIsAll && bIsAll) return true;
	return sameIdSet(a, b);
};

/**
 * True when two L/Z/S filter selections represent the same filter.
 * Treats semantic "all selected" (including pre-reconcile `[]`) as equivalent
 * to explicit full ID lists.
 *
 * Used by: `location-zone-sensor-filter` (pending vs committed Apply diff).
 */
export const areLocationZoneSensorFiltersEquivalent = (
	a: LocationZoneSensorFilterIds,
	b: LocationZoneSensorFilterIds,
	idSets: LocationZoneSensorIdSets,
): boolean => {
	const aIsAll = !isLocationZoneSensorFilterActive(a, idSets);
	const bIsAll = !isLocationZoneSensorFilterActive(b, idSets);
	if (aIsAll && bIsAll) return true;
	return (
		sameIdSet(a.locationsFilter, b.locationsFilter) &&
		sameIdSet(a.zonesFilter, b.zonesFilter) &&
		sameIdSet(a.sensorsFilter, b.sensorsFilter)
	);
};

/**
 * Returns only IDs from `ids` that still exist in `validIds`.
 *
 * Used by: `reconcileFilterIds` and `filter-url-params` when parsing L/Z/S
 * values from the URL against the current org tree.
 */
export function filterValidIds(
	ids: readonly string[],
	validIds: readonly string[],
): string[] {
	if (validIds.length === 0) return [];
	const valid = new Set(validIds);
	return ids.filter((id) => valid.has(id));
}

/**
 * Walks the org location tree and collects every location, zone, and sensor ID.
 *
 * Used by: `useLocationZoneSensorIdSets` (memoized hook in `hooks/useOrgLocationTree.ts`),
 * `useValidateLocationZoneSensorFilters`, and tests.
 */
export function getLocationZoneSensorIdSets(
	locations: readonly Location[],
): LocationZoneSensorIdSets {
	const locationIds: string[] = [];
	const zoneIds: string[] = [];
	const sensorIds: string[] = [];

	for (const location of locations) {
		locationIds.push(location.id);
		for (const zone of location.zones ?? []) {
			zoneIds.push(zone.id);
			for (const sensor of zone.sensors ?? []) {
				sensorIds.push(sensor.id);
			}
		}
	}

	return { locationIds, sensorIds, zoneIds };
}

/**
 * Zone IDs that belong to the given location IDs (hierarchical scope).
 *
 * Used by: `reconcileLocationZoneSensorFilters` when locations are partially
 * selected and zone/sensor arrays need defaulting to the scoped subtree.
 */
export const getZoneIdsForLocations = (
	locations: readonly Location[],
	locationIds: readonly string[],
): string[] => {
	const locationIdSet = new Set(locationIds);
	const zoneIds: string[] = [];
	for (const location of locations) {
		if (locationIdSet.has(location.id)) {
			for (const zone of location.zones ?? []) {
				zoneIds.push(zone.id);
			}
		}
	}
	return zoneIds;
};

/**
 * Sensor IDs that belong to the given location IDs (hierarchical scope).
 *
 * Used by: `reconcileLocationZoneSensorFilters` when locations are partially
 * selected and zone/sensor arrays need defaulting to the scoped subtree.
 */
export const getSensorIdsForLocations = (
	locations: readonly Location[],
	locationIds: readonly string[],
): string[] => {
	const locationIdSet = new Set(locationIds);
	const sensorIds: string[] = [];
	for (const location of locations) {
		if (locationIdSet.has(location.id)) {
			for (const zone of location.zones ?? []) {
				for (const sensor of zone.sensors ?? []) {
					sensorIds.push(sensor.id);
				}
			}
		}
	}
	return sensorIds;
};

/**
 * Normalizes a single L/Z/S ID array against the current org tree:
 * - Strips IDs that no longer exist
 * - Legacy `[]` (or all-invalid) expands to the full `allIds` list (default = all)
 * - When every stored ID is still valid and the org grew, new IDs are included
 *
 * Used by: `reconcileLocationZoneSensorFilters`.
 */
export const reconcileFilterIds = (
	filterIds: readonly string[],
	allIds: readonly string[],
): string[] => {
	if (allIds.length === 0) return [];
	const valid = filterValidIds(filterIds, allIds);
	if (valid.length === 0) return [...allIds];
	if (sameIdSet(valid, allIds)) return [...allIds];
	return valid;
};

/** The org tree a filter was last reconciled against. */
export type PreviousLocationTree = {
	idSets: LocationZoneSensorIdSets;
	locations: readonly Location[];
};

/**
 * Reconciles all three L/Z/S filters together after the location tree loads or changes.
 * Handles cross-level consistency: empty zone/sensor arrays mean "all under selected
 * locations" vs "all in org" depending on whether every location is selected.
 *
 * When `previous` is given, a dimension that covered everything in the previous
 * tree (the whole org, or everything under the selected locations) grows to
 * cover what was added since — a new camera, zone or location. Without this the
 * old full list reads as a partial selection and hides the new item's events.
 *
 * Used by: `useValidateLocationZoneSensorFilters` (mounted from `EventsFilterValidation`
 * in `events-provider.tsx`) to hydrate context from localStorage, URL, or stale state.
 */
export const reconcileLocationZoneSensorFilters = (
	filters: LocationZoneSensorFilterIds,
	locations: readonly Location[],
	idSets: LocationZoneSensorIdSets,
	previous?: PreviousLocationTree,
): LocationZoneSensorFilterIds => {
	const coveredAllLocations =
		!!previous &&
		previous.idSets.locationIds.length > 0 &&
		sameIdSet(filters.locationsFilter, previous.idSets.locationIds);

	const locationsFilter = coveredAllLocations
		? [...idSets.locationIds]
		: reconcileFilterIds(filters.locationsFilter, idSets.locationIds);

	const allLocationsSelected = isAllFilterIdsSelected(
		locationsFilter,
		idSets.locationIds,
	);
	const scopedZoneIds = getZoneIdsForLocations(locations, locationsFilter);
	const scopedSensorIds = getSensorIdsForLocations(locations, locationsFilter);

	// Everything this dimension could select in the previous tree, for the
	// same location selection.
	const previousZoneScope = previous
		? coveredAllLocations
			? previous.idSets.zoneIds
			: getZoneIdsForLocations(previous.locations, filters.locationsFilter)
		: [];
	const previousSensorScope = previous
		? coveredAllLocations
			? previous.idSets.sensorIds
			: getSensorIdsForLocations(previous.locations, filters.locationsFilter)
		: [];

	const zonesFilter =
		filters.zonesFilter.length === 0
			? allLocationsSelected
				? reconcileFilterIds([], idSets.zoneIds)
				: [...scopedZoneIds]
			: previousZoneScope.length > 0 &&
				  sameIdSet(filters.zonesFilter, previousZoneScope)
				? allLocationsSelected
					? [...idSets.zoneIds]
					: [...scopedZoneIds]
				: reconcileFilterIds(filters.zonesFilter, idSets.zoneIds);

	const sensorsFilter =
		filters.sensorsFilter.length === 0
			? allLocationsSelected
				? reconcileFilterIds([], idSets.sensorIds)
				: [...scopedSensorIds]
			: previousSensorScope.length > 0 &&
				  sameIdSet(filters.sensorsFilter, previousSensorScope)
				? allLocationsSelected
					? [...idSets.sensorIds]
					: [...scopedSensorIds]
				: reconcileFilterIds(filters.sensorsFilter, idSets.sensorIds);

	return { locationsFilter, sensorsFilter, zonesFilter };
};
